/**
 * LivePhotoCapture — Browser camera capture for Document Studio
 *
 * State machine:
 *   IDLE → REQUESTING_PERMISSION → PERMISSION_DENIED
 *                                → PREVIEWING (live video feed)
 *                                  → CAPTURED (frozen frame + blob)
 *                                    → [Retake] → PREVIEWING
 *                                    → [Use Photo] → UPLOADING → DONE | UPLOAD_ERROR
 *
 * Captured JPEG is uploaded via useCaptureUpload (presigned S3, audit trail).
 * Uploaded document enters the chaos pipeline → AI vision analysis (Bedrock Claude).
 *
 * DRR-128: only the photo blob is written, no raw stream is persisted.
 * DRR-081: image/jpeg goes through vision analysis path (not text chaos path).
 * Mantine v8.x only.
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Title,
  ThemeIcon,
  Button,
  Alert,
  Progress,
  Badge,
  Box,
  Center,
} from '@mantine/core';
import {
  IconCamera,
  IconCameraOff,
  IconRefresh,
  IconCheck,
  IconAlertCircle,
  IconPlayerRecord,
} from '@tabler/icons-react';
import { useCaptureUpload } from './hooks/useCaptureUpload';
import type { UploadResult } from './hooks/useChaosDocumentUpload';

// ============================================================
// Types
// ============================================================

type LivePhotoPhase =
  | 'IDLE'
  | 'REQUESTING_PERMISSION'
  | 'PERMISSION_DENIED'
  | 'PREVIEWING'
  | 'CAPTURED'
  | 'UPLOADING'
  | 'UPLOAD_ERROR'
  | 'DONE';

interface LivePhotoState {
  phase: LivePhotoPhase;
  stream?: MediaStream;
  blob?: Blob;
  previewUrl?: string;
  progress?: number;
  errorMessage?: string;
}

export interface LivePhotoCaptureProps {
  onUploadComplete?: (results: UploadResult[]) => void;
  onError?: (error: Error) => void;
  sourceSystem?: string;
}

// ============================================================
// Component
// ============================================================

export const LivePhotoCapture: React.FC<LivePhotoCaptureProps> = ({
  onUploadComplete,
  onError,
  sourceSystem = 'document-studio-photo',
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<LivePhotoState>({ phase: 'IDLE' });

  const { uploadBlob, uploadProgress, isUploading, error: uploadError } = useCaptureUpload();

  // Stop all camera tracks when component unmounts
  useEffect(() => {
    return () => {
      state.stream?.getTracks().forEach((t) => t.stop());
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Attach stream to video element when entering PREVIEWING
  useEffect(() => {
    if (state.phase === 'PREVIEWING' && state.stream && videoRef.current) {
      videoRef.current.srcObject = state.stream;
    }
  }, [state.phase, state.stream]);

  const openCamera = useCallback(async () => {
    setState({ phase: 'REQUESTING_PERMISSION' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setState({ phase: 'PREVIEWING', stream });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Camera access denied';
      setState({ phase: 'PERMISSION_DENIED', errorMessage: msg });
    }
  }, []);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !state.stream) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const previewUrl = URL.createObjectURL(blob);
        // Stop stream while reviewing — retake will re-open
        state.stream?.getTracks().forEach((t) => t.stop());
        setState({ phase: 'CAPTURED', blob, previewUrl });
      },
      'image/jpeg',
      0.92
    );
  }, [state.stream]);

  const retake = useCallback(async () => {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    setState({ phase: 'REQUESTING_PERMISSION' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setState({ phase: 'PREVIEWING', stream });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Camera access denied';
      setState({ phase: 'PERMISSION_DENIED', errorMessage: msg });
    }
  }, [state.previewUrl]);

  const usePhoto = useCallback(async () => {
    if (!state.blob) return;
    setState((s) => ({ ...s, phase: 'UPLOADING' }));
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `photo-capture-${timestamp}.jpg`;
      const result = await uploadBlob(state.blob, filename, { sourceSystem });
      setState((s) => ({ ...s, phase: 'DONE' }));
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
      onUploadComplete?.([result]);
    } catch (err) {
      const uploadErr = err instanceof Error ? err : new Error('Upload failed');
      setState((s) => ({ ...s, phase: 'UPLOAD_ERROR', errorMessage: uploadErr.message }));
      onError?.(uploadErr);
    }
  }, [state.blob, state.previewUrl, uploadBlob, sourceSystem, onUploadComplete, onError]);

  const reset = useCallback(() => {
    state.stream?.getTracks().forEach((t) => t.stop());
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    setState({ phase: 'IDLE' });
  }, [state.stream, state.previewUrl]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group gap="xs">
          <ThemeIcon size="lg" variant="light" color="blue">
            <IconCamera size={20} />
          </ThemeIcon>
          <div>
            <Title order={4}>Live Photo Capture</Title>
            <Text size="sm" c="dimmed">
              Capture a photo with your device camera for AI vision analysis
            </Text>
          </div>
          {state.phase !== 'IDLE' && state.phase !== 'DONE' && (
            <Badge ml="auto" color="blue" variant="light">
              {state.phase.replace(/_/g, ' ')}
            </Badge>
          )}
        </Group>

        {/* IDLE */}
        {state.phase === 'IDLE' && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={60} radius="xl" variant="light" color="gray">
                <IconCamera size={30} />
              </ThemeIcon>
              <Text size="lg" fw={500}>Ready to capture</Text>
              <Text size="sm" c="dimmed">
                Your browser will request camera permission
              </Text>
              <Button leftSection={<IconCamera size={16} />} onClick={openCamera} mt="xs">
                Open Camera
              </Button>
            </Stack>
          </Center>
        )}

        {/* REQUESTING_PERMISSION */}
        {state.phase === 'REQUESTING_PERMISSION' && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Text c="dimmed">Requesting camera access…</Text>
            </Stack>
          </Center>
        )}

        {/* PERMISSION_DENIED */}
        {state.phase === 'PERMISSION_DENIED' && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Camera Access Denied">
            <Stack gap="xs">
              <Text size="sm">{state.errorMessage}</Text>
              <Text size="xs" c="dimmed">
                Allow camera access in your browser settings, then try again.
              </Text>
              <Button size="xs" variant="light" onClick={reset}>
                Back
              </Button>
            </Stack>
          </Alert>
        )}

        {/* PREVIEWING — live video feed */}
        {state.phase === 'PREVIEWING' && (
          <Stack gap="sm">
            <Box
              style={{
                position: 'relative',
                borderRadius: 8,
                overflow: 'hidden',
                background: '#000',
                aspectRatio: '16/9',
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
            <Group justify="center">
              <Button
                leftSection={<IconPlayerRecord size={16} />}
                onClick={capturePhoto}
                color="blue"
                size="md"
              >
                Capture Photo
              </Button>
              <Button variant="subtle" color="gray" onClick={reset}>
                Cancel
              </Button>
            </Group>
          </Stack>
        )}

        {/* CAPTURED — review frozen frame */}
        {state.phase === 'CAPTURED' && state.previewUrl && (
          <Stack gap="sm">
            <Box
              style={{
                borderRadius: 8,
                overflow: 'hidden',
                aspectRatio: '16/9',
              }}
            >
              <img
                src={state.previewUrl}
                alt="Captured photo"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
            <Group justify="center">
              <Button
                leftSection={<IconCheck size={16} />}
                onClick={usePhoto}
                color="green"
              >
                Use Photo
              </Button>
              <Button
                leftSection={<IconRefresh size={16} />}
                variant="light"
                onClick={retake}
              >
                Retake
              </Button>
            </Group>
          </Stack>
        )}

        {/* UPLOADING */}
        {state.phase === 'UPLOADING' && (
          <Stack gap="sm">
            {state.previewUrl && (
              <Box style={{ borderRadius: 8, overflow: 'hidden', aspectRatio: '16/9' }}>
                <img
                  src={state.previewUrl}
                  alt="Uploading photo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.6 }}
                />
              </Box>
            )}
            <Progress value={uploadProgress} animated size="sm" />
            <Text ta="center" size="sm" c="dimmed">
              Uploading for AI vision analysis… {Math.round(uploadProgress)}%
            </Text>
          </Stack>
        )}

        {/* UPLOAD_ERROR */}
        {state.phase === 'UPLOAD_ERROR' && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Upload Failed">
            <Stack gap="xs">
              <Text size="sm">{state.errorMessage ?? uploadError?.message}</Text>
              <Group>
                <Button size="xs" color="blue" onClick={usePhoto}>
                  Retry
                </Button>
                <Button size="xs" variant="subtle" onClick={reset}>
                  Start Over
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {/* DONE */}
        {state.phase === 'DONE' && (
          <Alert color="green" icon={<IconCheck size={16} />} title="Photo Uploaded">
            <Group justify="space-between">
              <Text size="sm">Photo queued for AI vision analysis.</Text>
              <Button size="xs" variant="light" leftSection={<IconCamera size={14} />} onClick={reset}>
                Capture Another
              </Button>
            </Group>
          </Alert>
        )}

        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Stack>
    </Card>
  );
};
